import React, { useState } from 'react';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import type { IFactorBreakdownProps } from './types';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
import { DBO_U } from '@op/utils/format';
import ChannelLabel from '@op/components/ChannelLabel';
import CommonBubble from '@home/components/CommonBubble';

const COMMENT_TYPE_MAP: Record<string, string> = {
  typical_discussion: '典型讨论',
  viral_post: '发酵主贴',
  kol: '最早KOL',
  hot_comment: '热门讨论',
};

const COMMENT_TYPE_ORDER = Object.keys(COMMENT_TYPE_MAP);

export default function FactorBreakdown({ factors }: IFactorBreakdownProps) {
  const { formatMessage } = useLanguage();
  const [expandedSet, setExpandedSet] = useState<Set<number>>(new Set());

  const toggleExpand = (index: number) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  if (!factors || factors.length === 0) {
    return null;
  }

  return (
    <div className="dbo-event-detail-factors">
      <div className="dbo-event-detail-section__title dbo-event-detail-section__title--gradient">
        <span className="dbo-event-detail-section__title-icon"></span>
        <span className="dbo-event-detail-section__title-text">
          {formatMessage('Attribution Breakdown')}
        </span>
      </div>
      <div className="dbo-event-detail-factors__list">
        {[...factors]
          .sort((a, b) => (b.contribution ?? 0) - (a.contribution ?? 0))
          .map((factor, index) => (
            <div key={index} className="dbo-event-detail-factors__item">
              <div className="dbo-event-detail-factors__header">
                <span className="dbo-event-detail-factors__title">
                  {factor.name}
                </span>
                <PercentageItem
                  percentage={factor.contribution}
                  showIcon={true}
                />
              </div>
              <div className="dbo-event-detail-factors__content">
                {factor.detail}
              </div>

              {/* 评论展开： --open */}
              <div
                className={`dbo-event-detail-factors-more${expandedSet.has(index) ? ' --open' : ''
                  }`}
                onClick={() => toggleExpand(index)}
              >
                {formatMessage('Representative Reviews')}
              </div>
              {expandedSet.has(index) && (
                <div className="dbo-event-factors-comment">
                  {[...(factor?.comment_list ?? [])]
                    .sort((a, b) => {
                      const ai = COMMENT_TYPE_ORDER.indexOf(a.comment_type);
                      const bi = COMMENT_TYPE_ORDER.indexOf(b.comment_type);
                      return (
                        (ai === -1 ? Infinity : ai) -
                        (bi === -1 ? Infinity : bi)
                      );
                    })
                    .map((comment) => {
                      const typeLabel =
                        COMMENT_TYPE_MAP[comment.comment_type] ||
                        comment.comment_type;
                      return (
                        <div
                          key={typeLabel}
                          className="dbo-event-factors-comment-item"
                          onClick={() => DBO_U.open(comment.url)}
                        >
                          <CommonBubble content={formatMessage({ id: 'Click to view details' })}>
                            <div className="dbo-event-factors-comment-item-hd">
                              <span className="dbo-event-factors-comment-item-type">{typeLabel}</span>
                              <ChannelLabel
                                channel_name={comment.channel as any}
                              ></ChannelLabel>
                            </div>
                            <div className="dbo-event-factors-comment-item-content">
                              {comment.summary}
                            </div>
                          </CommonBubble>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
